/**
 * Forgot password — email OTP verification then new password.
 * Steps: 1) enter email → 2) enter code + new password.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, EyeOff, KeyRound, Mail, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import EmailDomainField, { buildEmailAddress } from './EmailDomainField';

const OTP_LEN = 6;

function PasswordField({ label, value, onChange, required = true }) {
    const [show, setShow] = useState(false);
    return (
        <div>
            <label className="ag-label">{label}</label>
            <div className="relative">
                <input
                    className="ag-input pr-10"
                    type={show ? 'text' : 'password'}
                    required={required}
                    minLength={8}
                    autoComplete="new-password"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                />
                <button
                    type="button"
                    onClick={() => setShow(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-ink-500 hover:text-ink-800 hover:bg-ink-100"
                    aria-label={show ? 'Hide password' : 'Show password'}
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
        </div>
    );
}

export default function ForgotPassword() {
    const nav = useNavigate();
    const [step, setStep] = useState('email');
    const [busy, setBusy] = useState(false);
    const [resending, setResending] = useState(false);
    const [devOtp, setDevOtp] = useState(null);
    const [ttlMinutes, setTtlMinutes] = useState(10);

    const [email, setEmail] = useState('');
    const [emailLocal, setEmailLocal] = useState('');
    const [emailDomain, setEmailDomain] = useState('');
    const [domainOptions, setDomainOptions] = useState([]);
    const [enforceDomain, setEnforceDomain] = useState(false);

    const [digits, setDigits] = useState(Array(OTP_LEN).fill(''));
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const otpRefs = useRef([]);

    useEffect(() => {
        api.get('/auth/directory')
            .then(r => {
                const domains = r.data?.emailDomains || [];
                setDomainOptions(domains);
                setEnforceDomain(!!r.data?.enforceEmailDomain && domains.length > 0);
                if (domains.length > 0) setEmailDomain(domains[0].slug);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (step === 'reset') otpRefs.current[0]?.focus();
    }, [step]);

    const resolvedEmail = () => buildEmailAddress({
        email,
        emailLocal,
        emailDomain,
        enforceDomain,
    });

    const setDigit = (i, v) => {
        const ch = v.replace(/\D/g, '').slice(-1);
        const next = [...digits];
        next[i] = ch;
        setDigits(next);
        if (ch && i < OTP_LEN - 1) otpRefs.current[i + 1]?.focus();
    };

    const onOtpPaste = (e) => {
        const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LEN);
        if (!text) return;
        e.preventDefault();
        const next = Array(OTP_LEN).fill('');
        text.split('').forEach((c, i) => { next[i] = c; });
        setDigits(next);
        otpRefs.current[Math.min(text.length, OTP_LEN - 1)]?.focus();
    };

    const onOtpKey = (i, e) => {
        if (e.key === 'Backspace' && !digits[i] && i > 0) otpRefs.current[i - 1]?.focus();
    };

    const requestCode = async (e) => {
        e?.preventDefault();
        const addr = resolvedEmail();
        if (!addr) return toast.error('Enter your work email');
        setBusy(true);
        try {
            const { data } = await api.post('/auth/forgot-password/request', { email: addr });
            setTtlMinutes(data.ttlMinutes || 10);
            if (data.devOtp) setDevOtp(data.devOtp);
            toast.success(data.message || 'Check your email for a reset code');
            setStep('reset');
            setDigits(Array(OTP_LEN).fill(''));
        } catch { /* toasted */ }
        finally { setBusy(false); }
    };

    const resendCode = async () => {
        const addr = resolvedEmail();
        if (!addr) return;
        setResending(true);
        try {
            const { data } = await api.post('/auth/forgot-password/request', { email: addr });
            if (data.devOtp) setDevOtp(data.devOtp);
            toast.success('New code sent');
            setDigits(Array(OTP_LEN).fill(''));
            otpRefs.current[0]?.focus();
        } catch { /* toasted */ }
        finally { setResending(false); }
    };

    const resetPassword = async (e) => {
        e.preventDefault();
        const addr = resolvedEmail();
        const code = digits.join('');
        if (code.length !== OTP_LEN) return toast.error(`Enter all ${OTP_LEN} digits`);
        if (password.length < 8) return toast.error('Password must be at least 8 characters');
        if (password !== confirm) return toast.error('Passwords do not match');

        setBusy(true);
        try {
            const { data } = await api.post('/auth/forgot-password/reset', {
                email: addr,
                code,
                newPassword: password,
            });
            toast.success(data.message || 'Password updated');
            nav('/login');
        } catch { /* toasted */ }
        finally { setBusy(false); }
    };

    if (step === 'email') {
        return (
            <div className="space-y-5">
                <div>
                    <h2 className="text-2xl font-bold">Forgot password?</h2>
                    <p className="text-sm text-ink-500 mt-1">
                        Enter your work email and we&rsquo;ll send a 6-digit verification code.
                    </p>
                </div>

                <form onSubmit={requestCode} className="space-y-4">
                    <EmailDomainField
                        label="Work email"
                        enforceDomain={enforceDomain}
                        domainOptions={domainOptions}
                        email={email}
                        emailLocal={emailLocal}
                        emailDomain={emailDomain}
                        onChange={patch => {
                            if (patch.email !== undefined) setEmail(patch.email);
                            if (patch.emailLocal !== undefined) setEmailLocal(patch.emailLocal);
                            if (patch.emailDomain !== undefined) setEmailDomain(patch.emailDomain);
                        }}
                        required
                        placeholder="you"
                        hint={enforceDomain
                            ? `Use your company domain: ${domainOptions.map(d => '@' + d.slug).join(', ')}`
                            : undefined}
                    />

                    <button type="submit" className="ag-btn-primary w-full justify-center" disabled={busy}>
                        {busy ? 'Sending…' : 'Send reset code'}
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </form>

                <div className="text-center text-sm text-ink-500">
                    <Link to="/login" className="ag-link inline-flex items-center gap-1">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="text-center">
                <div className="h-12 w-12 rounded-full bg-brand-teal-50 text-brand-teal-700 grid place-items-center mx-auto">
                    <KeyRound className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold mt-4">Reset your password</h2>
                <p className="text-sm text-ink-500 mt-1 max-w-sm mx-auto">
                    Enter the code sent to <b>{resolvedEmail()}</b> and choose a new password.
                </p>
            </div>

            <form onSubmit={resetPassword} className="space-y-4">
                <div>
                    <label className="ag-label">Verification code</label>
                    <div className="flex justify-center gap-2 mt-1" onPaste={onOtpPaste}>
                        {digits.map((d, i) => (
                            <input
                                key={i}
                                ref={el => { otpRefs.current[i] = el; }}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={1}
                                value={d}
                                onChange={e => setDigit(i, e.target.value)}
                                onKeyDown={e => onOtpKey(i, e)}
                                className="w-11 h-12 text-center font-heading text-xl font-bold rounded-lg border-2 border-ink-300 focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30 tabular-nums"
                            />
                        ))}
                    </div>
                  
                    <div className="text-center mt-2">
                        <button
                            type="button"
                            onClick={resendCode}
                            disabled={resending}
                            className="text-xs text-ink-500 hover:text-brand-teal-700 inline-flex items-center gap-1"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} />
                            Resend code
                        </button>
                    </div>
                </div>

                <PasswordField label="New password *" value={password} onChange={setPassword} />
                <PasswordField label="Confirm password *" value={confirm} onChange={setConfirm} />

                <button type="submit" className="ag-btn-primary w-full justify-center" disabled={busy}>
                    {busy ? 'Updating…' : 'Update password'}
                    <ArrowRight className="h-4 w-4" />
                </button>
            </form>

            <p className="text-[11px] text-ink-500 text-center">
                Code expires in {ttlMinutes} minutes · 3 wrong attempts lock the code.
            </p>

            <div className="flex items-center justify-center gap-4 text-sm text-ink-500">
                <button
                    type="button"
                    className="ag-link inline-flex items-center gap-1"
                    onClick={() => { setStep('email'); setDigits(Array(OTP_LEN).fill('')); setDevOtp(null); }}
                >
                    <Mail className="h-3.5 w-3.5" /> Change email
                </button>
                <Link to="/login" className="ag-link inline-flex items-center gap-1">
                    <ArrowLeft className="h-3.5 w-3.5" /> Sign in
                </Link>
            </div>
        </div>
    );
}
